import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import {
  companiesForGroupsKey,
  companiesSimpleKey,
  fetchCompaniesForGroups,
  fetchCompaniesSimple,
} from './companyQueries'

type Which = 'simple' | 'forGroups' | 'both'

/**
 * Wstrzykuje uproszczone listy firm z react-query do kontenerów zakładek.
 *
 * W slice'ie `companiesBasic` i `companiesForGroups` były osobnymi polami karmionymi
 * osobnymi akcjami, a czytały je trzy ekrany w różnych kombinacjach — stąd parametr
 * zamiast trzech wariantów HOC-a. Nazwy propsów są zachowane, bo Grupy firm oczekują
 * listy pod `companies`, a Raporty pod `companiesBasic`.
 */
export function withCompanyLists<P extends object>(
  Wrapped: React.ComponentType<P>,
  which: Which,
  {
    simpleProp = 'companiesBasic',
    simpleFetchProp = 'getCompaniesSimple',
    forGroupsFetchProp = 'getCompaniesSimpleForGroups',
  }: {
    simpleProp?: string
    /** Ekrany wołają pobranie pod różnymi nazwami - stąd parametry, a nie stała nazwa. */
    simpleFetchProp?: string
    forGroupsFetchProp?: string
  } = {},
) {
  const WithCompanyLists = React.forwardRef<unknown, any>((props, ref) => {
    const queryClient = useQueryClient()
    const tenantId = (props as any).tenantId ?? null

    const simple = useQuery({
      queryKey: companiesSimpleKey(tenantId),
      queryFn: () => fetchCompaniesSimple(tenantId ?? undefined),
      enabled: which === 'simple' || which === 'both',
    })

    const forGroups = useQuery({
      queryKey: companiesForGroupsKey([]),
      queryFn: () => fetchCompaniesForGroups([]),
      enabled: which === 'forGroups' || which === 'both',
    })

    const getCompaniesSimple = React.useCallback(
      (id?: string) =>
        queryClient.fetchQuery({
          queryKey: companiesSimpleKey(id ?? null),
          queryFn: () => fetchCompaniesSimple(id),
        }),
      [queryClient],
    )

    const getCompaniesForGroups = React.useCallback(
      (ids: string[] = [], includeDeleted = false) =>
        queryClient.fetchQuery({
          queryKey: companiesForGroupsKey(ids),
          queryFn: () => fetchCompaniesForGroups(ids, includeDeleted),
        }),
      [queryClient],
    )

    const injected: Record<string, unknown> = {}
    if (which === 'simple' || which === 'both') {
      injected[simpleProp] = simple.data ?? []
      injected[simpleFetchProp] = getCompaniesSimple
    }
    if (which === 'forGroups' || which === 'both') {
      injected.companiesForGroups = forGroups.data ?? []
      injected[forGroupsFetchProp] = getCompaniesForGroups
    }

    return <Wrapped ref={ref as any} {...(props as P)} {...(injected as any)} />
  })

  WithCompanyLists.displayName = `withCompanyLists(${
    (Wrapped as any).displayName || (Wrapped as any).name || 'Component'
  })`

  return WithCompanyLists
}
